import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PhoneIcon } from '@/components/icons';
import { clickToCallApi } from '@/api';
import { Toast } from './Toast';
import styles from './CallInput.module.css';

export const CallInput: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isCalling, setIsCalling] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '' });
    const popoverRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Close popover on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                popoverRef.current &&
                !popoverRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Focus input when popover opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const isValidNumber = phoneNumber.replace(/\s/g, '').length === 10 && /^\d{10}$/.test(phoneNumber.replace(/\s/g, ''));

    const handleCall = useCallback(async () => {
        const agentId =
            localStorage.getItem('agent_id') ||
            localStorage.getItem('agentId');

        if (!agentId) {
            setToast({ show: true, message: 'Agent ID not found. Please log in again.' });
            return;
        }

        const cleanNumber = phoneNumber.replace(/\s/g, '');
        if (!cleanNumber || cleanNumber.length !== 10) {
            setToast({ show: true, message: 'Please enter a valid 10-digit phone number.' });
            return;
        }

        setIsCalling(true);
        try {
            const response = await clickToCallApi.clickToCall({
                agent_id: agentId,
                customer_number: '91' + cleanNumber,
                cp_team: 'sales_team',
                call_type: 'phone',
            });
            setToast({ show: true, message: response.message || 'Call initiated successfully' });
            setIsOpen(false);
            setPhoneNumber('');
        } catch {
            setToast({ show: true, message: 'Failed to initiate call. Please try again.' });
        } finally {
            setIsCalling(false);
        }
    }, [phoneNumber]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && isValidNumber && !isCalling) {
            handleCall();
        }
        if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Allow only digits and spaces, max 10 digits
        const value = e.target.value;
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length <= 10) {
            setPhoneNumber(digitsOnly);
        }
    };

    return (
        <>
            <div className={styles.callInputContainer}>
                <button
                    ref={buttonRef}
                    className={`${styles.callButton} ${isOpen ? styles.callButtonActive : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label="Make a call"
                    title="Make a call"
                >
                    <PhoneIcon size={18} />
                </button>

                {isOpen && (
                    <div ref={popoverRef} className={styles.popover}>
                        <div className={styles.popoverHeader}>Quick Call</div>
                        <div className={styles.popoverBody}>
                            <div className={styles.inputGroup}>
                                <span className={styles.countryCode}>+91</span>
                                <input
                                    ref={inputRef}
                                    type="tel"
                                    className={styles.phoneInput}
                                    placeholder="Enter phone number"
                                    value={phoneNumber}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    maxLength={10}
                                    disabled={isCalling}
                                />
                            </div>
                            <button
                                className={styles.dialButton}
                                onClick={handleCall}
                                disabled={!isValidNumber || isCalling}
                            >
                                {isCalling ? (
                                    <span className={styles.callingText}>Calling...</span>
                                ) : (
                                    <>
                                        <PhoneIcon size={14} />
                                        <span>Call</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <Toast
                show={toast.show}
                message={toast.message}
                onClose={() => setToast({ show: false, message: '' })}
            />
        </>
    );
};
